// deps
import React, { memo, useCallback, useMemo, useContext } from "react";

// internal-deps
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { PriceComparisonHalfCard, GOT_IT_CTA } from "@swiggy-private/cart-ui";
import { TOING_PC_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { IMAGE_UPLOADER_ERROR_TYPES, ImageUploader } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ImageUploader";
import { getBridgeSdk, GlobalHeaderState, NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// types
import { IToingPriceComparisonBEData, PC_HALFCARD_STATES, PreSignedUrlData } from "@swiggy-private/cart-types";

// helpers
import { getUploaderButtonAppearance, shouldShowBottomActions, shouldShowImageUploader } from "./helpers";

// styles
import { BottomActionsWrapper, ImageUploaderButtonInnerRow, OuterWrapper } from "./styles";

// constants
import {
    IMAGE_UPLOADER_BUTTON_BASE_STYLE,
    TOING_PC_MAX_FILE_SIZE_MB,
    TOING_PC_SUPPORTED_IMAGE_TYPES,
    UPLOAD_BUTTON_LABEL_BILL,
    ADD_IMAGE_CLOUDINARY_ID,
} from "./constants";

// hooks
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    impressionTutorialD2C,
    impressionScanningD2C,
    impressionResultD2C,
    clickResultRejectD2C,
    clickTutorialD2C,
} from "@cart-client/analytics/events/ToingLpg";

import { MediaUploadSourceType } from "@swiggy-private/native-bridge";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { isAndroidPlatform, isiOSPlatform } from "@swiggy-private/core-ui";

interface IToingPriceComparisonHalfCardProps {
    comparisonState: PC_HALFCARD_STATES;
    priceComparisonData?: IToingPriceComparisonBEData;
    onUploadBill: () => void;
    onClose: () => void;
    onCloseSilent: () => void;
    presignedData: PreSignedUrlData | null;
    updatePcHalfCardState: (state: PC_HALFCARD_STATES) => void;
    totalSavings: number;
}

const ToingPriceComparisonHalfCard: React.FC<IToingPriceComparisonHalfCardProps> = ({
    comparisonState,
    priceComparisonData,
    onUploadBill,
    onClose,
    onCloseSilent,
    presignedData,
    updatePcHalfCardState,
    totalSavings,
}) => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { presigned_url: preSignedUrl, aws_fields: awsFields } = presignedData ?? {};
    const { cartState } = useContext(CartContext);

    const shouldRenderImageUploader = useMemo(
        () => shouldShowImageUploader(comparisonState, priceComparisonData),
        [comparisonState, priceComparisonData],
    );

    const shouldRenderBottomActions = useMemo(
        () => shouldShowBottomActions(comparisonState, priceComparisonData),
        [comparisonState, priceComparisonData],
    );

    const buttonAppearance = useMemo(
        () => getUploaderButtonAppearance(comparisonState, priceComparisonData),
        [comparisonState, priceComparisonData],
    );

    const imageUploaderButtonDetails = useMemo(
        () => ({
            style: {
                ...IMAGE_UPLOADER_BUTTON_BASE_STYLE,
                backgroundColor: buttonAppearance?.bg,
            },
        }),
        [buttonAppearance?.bg],
    );

    const sdk = getBridgeSdk();
    const headers = GlobalHeaderState.getHeaders();
    const isPlatformIos = isiOSPlatform(headers.platform);
    const isPlatformAndroid = isAndroidPlatform(headers.platform);

    const onUploadStart = useCallback(() => {
        updatePcHalfCardState(PC_HALFCARD_STATES.UPLOAD_IMAGE);
    }, [updatePcHalfCardState]);

    const onTutorialCtaClick = useCallback(() => {
        clickTutorialD2C(clickEvent, {
            text: priceComparisonData?.instructions_data?.free_cash_multiplier ?? "",
            cta: UPLOAD_BUTTON_LABEL_BILL,
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
        });
    }, [
        clickEvent,
        priceComparisonData?.instructions_data?.free_cash_multiplier,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        presignedData?.request_id,
    ]);

    const onUploadError = useCallback(
        (error: { type: string; message: string }) => {
            try {
                if (error.type === IMAGE_UPLOADER_ERROR_TYPES.FILE_SIZE_TOO_LARGE.type) {
                    updatePcHalfCardState(PC_HALFCARD_STATES.IMAGE_TOO_LARGE_ERROR);
                    return;
                }
                updatePcHalfCardState(PC_HALFCARD_STATES.ERROR);
            } catch (error) {
                updatePcHalfCardState(PC_HALFCARD_STATES.ERROR);
            }
        },
        [updatePcHalfCardState],
    );

    const sendImageUploaderErrorToNewRelic = useCallback(
        (error: string, context: Record<string, unknown>) => {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TOING_PRICE_COMPARISON_IMAGE_UPLOAD_ERROR,
                error,
                context: JSON.stringify(context),
                comparisonState: String(comparisonState),
            });
        },
        [comparisonState],
    );

    const renderButtonContent = useCallback(() => {
        return (
            <ImageUploaderButtonInnerRow data-testid={TOING_PC_TEST_IDS.UPLOAD_CTA}>
                <Image cloudinaryId={ADD_IMAGE_CLOUDINARY_ID} alt="add image" width="16px" height="16px" />
                <Text color="text_inverse_Highest_Emphasis" variant="V2.body2.neutral.bold">
                    {buttonAppearance?.text}
                </Text>
            </ImageUploaderButtonInnerRow>
        );
    }, [buttonAppearance?.text]);

    const onGotItClick = useCallback(() => {
        clickResultRejectD2C(clickEvent, {
            text: priceComparisonData?.rejection_state_halfcard_data?.title ?? "",
            subText: priceComparisonData?.rejection_state_halfcard_data?.subtitle ?? "",
            cta: GOT_IT_CTA.text,
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
        });
        onCloseSilent();
    }, [
        clickEvent,
        onCloseSilent,
        priceComparisonData?.rejection_state_halfcard_data,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        presignedData?.request_id,
    ]);

    const onTutorialImpression = useCallback(() => {
        impressionTutorialD2C(impressionEvent, {
            text: priceComparisonData?.instructions_data?.free_cash_multiplier ?? "",
            cta: UPLOAD_BUTTON_LABEL_BILL,
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
        });
    }, [
        impressionEvent,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        priceComparisonData?.instructions_data?.free_cash_multiplier,
        presignedData?.request_id,
    ]);

    const onScanningImpression = useCallback(() => {
        impressionScanningD2C(impressionEvent, {
            state: PC_HALFCARD_STATES.SCANNING,
            text: "",
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
        });
    }, [impressionEvent, cartState?.localCart?.cart_id, cartState?.localCart?.restaurantId, presignedData?.request_id]);

    const onResultImpression = useCallback(() => {
        impressionResultD2C(impressionEvent, {
            text: priceComparisonData?.rejection_state_halfcard_data?.title ?? "",
            subText: priceComparisonData?.rejection_state_halfcard_data?.subtitle ?? "",
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
            pricingDetails: {
                prevBillAmt: priceComparisonData?.success_state_halfcard_data?.prev_bill_amt ?? "",
                currentBillAmt: priceComparisonData?.success_state_halfcard_data?.current_bill_amt ?? "",
                freeCashAmt: priceComparisonData?.success_state_halfcard_data?.free_cash_amt ?? "",
                diffInAmt: priceComparisonData?.success_state_halfcard_data?.diff_in_amt ?? "",
                totalSavings: String(totalSavings ?? ""),
            },
        });
    }, [
        impressionEvent,
        priceComparisonData?.rejection_state_halfcard_data,
        priceComparisonData?.success_state_halfcard_data,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        presignedData?.request_id,
        totalSavings,
    ]);

    return (
        <HalfCard isOpen onClose={onClose} closeOnOverlayClick variant="v3" hasCustomPadding overflowY="visible">
            <OuterWrapper>
                <PriceComparisonHalfCard
                    comparisonState={comparisonState}
                    priceComparisonData={priceComparisonData}
                    totalSavings={totalSavings}
                    onImpression={onTutorialImpression}
                    onScanningImpression={onScanningImpression}
                    onResultImpression={onResultImpression}
                />
                {shouldRenderBottomActions && (
                    <BottomActionsWrapper isAndroid={isPlatformAndroid} isIOS={isPlatformIos}>
                        {shouldRenderImageUploader && (
                            <div onClickCapture={onTutorialCtaClick}>
                                <ImageUploader
                                    supportedImageTypes={[...TOING_PC_SUPPORTED_IMAGE_TYPES]}
                                    presignedUrl={preSignedUrl ?? ""}
                                    awsFields={awsFields ?? {}}
                                    buttonDetails={imageUploaderButtonDetails}
                                    buttonText={renderButtonContent}
                                    sdk={sdk}
                                    isPlatformIos={isPlatformIos}
                                    onUploadSuccess={onUploadBill}
                                    onUploadError={onUploadError}
                                    sendErrorToNewRelic={sendImageUploaderErrorToNewRelic}
                                    skipNativeHalfCard
                                    maxFileSizeInMB={TOING_PC_MAX_FILE_SIZE_MB}
                                    isPlatformAndroid={isPlatformAndroid}
                                    swiggyHeaders={headers}
                                    supportedModes={[MediaUploadSourceType.gallery]}
                                    onUploadStart={onUploadStart}
                                />
                            </div>
                        )}
                        {!shouldRenderImageUploader && (
                            <SuperButton
                                variant="fullwidth"
                                buttonType="tertiary"
                                buttonSize="medium"
                                onClick={onGotItClick}
                                data-testid="toing-pc-got-it-cta"
                            >
                                {GOT_IT_CTA.text}
                            </SuperButton>
                        )}
                    </BottomActionsWrapper>
                )}
            </OuterWrapper>
        </HalfCard>
    );
};

export default memo(ToingPriceComparisonHalfCard);
